/**
 * cast.jsx — the cast: people, pets, products and places Rammy can draw again.
 *
 * Each subject holds up to four reference photos. Name one in a prompt ("draw
 * Maya reading on the beach") and those photos travel with the request, so the
 * same face — or the same mug — shows up across a whole set of pictures.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import * as L from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const KINDS = [
  { id: "person", label: "Person", icon: L.User },
  { id: "pet", label: "Pet", icon: L.PawPrint },
  { id: "product", label: "Product", icon: L.Package },
  { id: "place", label: "Place", icon: L.MapPin },
  { id: "style", label: "Style", icon: L.Palette },
];

const fileToBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onerror = rej;
  r.onload = () => res(String(r.result).split(",")[1]);
  r.readAsDataURL(file);
});

const kindOf = (id) => KINDS.find(k => k.id === id) || KINDS[0];

export function CastScreen() {
  const [cast, setCast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxPhotos, setMaxPhotos] = useState(4);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("person");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);       // [{file, preview}]
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const newFileRef = useRef(null);
  const addFileRef = useRef(null);
  const addTargetRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/cast");
      setCast(data.cast || []);
      setMaxPhotos(data.max_photos || 4);
    } catch (e) {
      console.warn("cast load failed:", e);
      toast.error("Couldn't load your cast.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const pickFiles = (list) => {
    const picked = Array.from(list || [])
      .filter(f => f.type?.startsWith("image/"))
      .slice(0, maxPhotos - files.length)
      .map(f => ({ file: f, preview: URL.createObjectURL(f) }));
    if (picked.length) setFiles(f => [...f, ...picked]);
  };

  const create = async () => {
    if (!name.trim()) return toast.error("Give them a name — that's how you'll ask for them.");
    if (!files.length) return toast.error("Add at least one photo.");
    setBusy(true);
    try {
      const photos = await Promise.all(files.map(f => fileToBase64(f.file)));
      await api.post("/cast", { name: name.trim(), kind, notes: notes.trim() || null, photos });
      toast.success(`${name.trim()} is in the cast — just say their name.`);
      setName(""); setNotes(""); setKind("person"); setFiles([]); setAdding(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that subject.");
    } finally {
      setBusy(false);
    }
  };

  const addPhotos = async (id, list) => {
    const picked = Array.from(list || []).filter(f => f.type?.startsWith("image/"));
    if (!picked.length) return;
    setBusyId(id);
    try {
      const photos = await Promise.all(picked.map(fileToBase64));
      await api.post(`/cast/${id}/photos`, { photos });
      toast.success("Another angle helps — they'll look more like themselves.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add that photo.");
    } finally {
      setBusyId(null);
    }
  };

  const dropPhoto = async (id, idx) => {
    setBusyId(id);
    try {
      await api.delete(`/cast/${id}/photos/${idx}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove that photo.");
    } finally {
      setBusyId(null);
    }
  };

  const retire = async (item) => {
    setBusyId(item.id);
    try {
      await api.delete(`/cast/${item.id}`);
      toast.success(`${item.name} retired.`);
      load();
    } catch (e) {
      toast.error("Couldn't retire that subject.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-8 space-y-6" data-testid="screen-cast">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl">Cast</h1>
          <p className="text-sm text-[var(--rammy-text-sec)] mt-1">
            The people, pets, products and places I can draw again. Name one in a
            prompt and they'll look like themselves.
          </p>
        </div>
        <Button onClick={() => setAdding(a => !a)} data-testid="cast-add-toggle">
          {adding ? <><L.X size={15} className="mr-1.5"/>Cancel</> : <><L.Plus size={15} className="mr-1.5"/>Add subject</>}
        </Button>
      </div>

      {adding && (
        <div className="rounded-2xl border border-[var(--rammy-border)] bg-[var(--rammy-surface)] p-5 space-y-4"
             data-testid="cast-add-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)]">
                What should I call them?
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Maya"
                maxLength={40}
                className="mt-1.5 w-full rounded-xl border border-[var(--rammy-border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--rammy-cyan)]"
                data-testid="cast-name-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)]">
                What are they?
              </label>
              <div className="mt-1.5 inline-flex flex-wrap gap-1.5">
                {KINDS.map(k => (
                  <button
                    key={k.id}
                    onClick={() => setKind(k.id)}
                    data-active={kind === k.id ? "true" : "false"}
                    data-testid={`cast-kind-${k.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      kind === k.id
                        ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10 text-[var(--rammy-text)]"
                        : "border-[var(--rammy-border)] text-[var(--rammy-text-sec)]"
                    }`}
                  >
                    <k.icon size={12} className="inline mr-1 -mt-0.5"/>{k.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)]">
              Anything I should remember <span className="normal-case tracking-normal font-normal opacity-70">optional</span>
            </label>
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="curly hair, always in a denim jacket"
              maxLength={300}
              className="mt-1.5 w-full rounded-xl border border-[var(--rammy-border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--rammy-cyan)]"
              data-testid="cast-notes-input"
            />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)] mb-2">
              Photos <span className="normal-case tracking-normal font-normal opacity-70">up to {maxPhotos} · a couple of angles is plenty</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {files.map((f, i) => (
                <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-[var(--rammy-border)]">
                  <img src={f.preview} alt="" className="w-full h-full object-cover"/>
                  <button
                    onClick={() => setFiles(x => x.filter((_, k) => k !== i))}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"
                    data-testid={`cast-new-photo-remove-${i}`}
                    aria-label="Remove photo"
                  >
                    <L.X size={11}/>
                  </button>
                </div>
              ))}
              {files.length < maxPhotos && (
                <button
                  onClick={() => newFileRef.current?.click()}
                  className="w-20 h-20 rounded-xl border border-dashed border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] text-[var(--rammy-text-muted)] flex flex-col items-center justify-center"
                  data-testid="cast-new-photo-add"
                >
                  <L.ImagePlus size={17}/>
                  <span className="text-[9px] uppercase tracking-widest mt-0.5">Add</span>
                </button>
              )}
              <input ref={newFileRef} type="file" accept="image/*" multiple hidden
                     onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }}
                     data-testid="cast-new-photo-input"/>
            </div>
          </div>

          <Button onClick={create} disabled={busy} data-testid="cast-create-btn">
            {busy ? <><L.Loader2 size={15} className="mr-1.5 animate-spin"/>Saving…</> : <>Add to cast</>}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[var(--rammy-text-muted)]" data-testid="cast-loading">Loading…</div>
      ) : cast.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--rammy-border)] p-10 text-center"
             data-testid="cast-empty">
          <L.Users size={22} className="mx-auto text-[var(--rammy-text-muted)]"/>
          <p className="mt-3 text-sm text-[var(--rammy-text-sec)]">
            Nobody in the cast yet. Add a person, a pet or a product and I'll keep
            them consistent across every picture.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="cast-list">
          {cast.map(item => {
            const k = kindOf(item.kind);
            return (
              <div key={item.id}
                   className="rounded-2xl border border-[var(--rammy-border)] bg-[var(--rammy-surface)] p-4"
                   data-testid={`cast-card-${item.id}`}>
                <div className="flex items-start gap-3">
                  <img
                    src={`${API_BASE}${item.photos[0]}`}
                    alt={item.name}
                    className="w-14 h-14 rounded-xl object-cover border border-[var(--rammy-border)]"
                    data-testid={`cast-avatar-${item.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate" data-testid={`cast-name-${item.id}`}>{item.name}</div>
                    <div className="text-xs text-[var(--rammy-text-muted)]">
                      <k.icon size={11} className="inline mr-1 -mt-0.5"/>{k.label} · {item.photo_count} photo{item.photo_count === 1 ? "" : "s"}
                      {item.used_count > 0 && ` · in ${item.used_count} picture${item.used_count === 1 ? "" : "s"}`}
                    </div>
                    {item.notes && (
                      <div className="text-xs text-[var(--rammy-text-sec)] mt-1 truncate">{item.notes}</div>
                    )}
                  </div>
                  <button
                    onClick={() => retire(item)}
                    disabled={busyId === item.id}
                    className="text-[var(--rammy-text-muted)] hover:text-red-500 disabled:opacity-40"
                    aria-label={`Retire ${item.name}`}
                    data-testid={`cast-retire-${item.id}`}
                  >
                    <L.Trash2 size={15}/>
                  </button>
                </div>

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {item.photos.map((p, i) => (
                    <div key={i} className="relative w-12 h-12 rounded-lg overflow-hidden border border-[var(--rammy-border)]">
                      <img src={`${API_BASE}${p}`} alt="" className="w-full h-full object-cover"/>
                      {item.photos.length > 1 && (
                        <button
                          onClick={() => dropPhoto(item.id, i)}
                          disabled={busyId === item.id}
                          className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 text-white flex items-center justify-center"
                          aria-label="Remove photo"
                          data-testid={`cast-photo-remove-${item.id}-${i}`}
                        >
                          <L.X size={9}/>
                        </button>
                      )}
                    </div>
                  ))}
                  {item.photo_count < maxPhotos && (
                    <button
                      onClick={() => { addTargetRef.current = item.id; addFileRef.current?.click(); }}
                      disabled={busyId === item.id}
                      className="w-12 h-12 rounded-lg border border-dashed border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] text-[var(--rammy-text-muted)] flex items-center justify-center disabled:opacity-40"
                      data-testid={`cast-photo-add-${item.id}`}
                    >
                      <L.Plus size={14}/>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          <input ref={addFileRef} type="file" accept="image/*" multiple hidden
                 onChange={(e) => { addPhotos(addTargetRef.current, e.target.files); e.target.value = ""; }}
                 data-testid="cast-photo-input"/>
        </div>
      )}
    </div>
  );
}

export default CastScreen;
